import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Typography,
  Divider,
  Backdrop,
  TextField,
  MenuItem,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { styled } from '@mui/material/styles'
import { makeGetRequest, makePostRequest } from '../../../utils/api'
import urls from '../../../utils/urls'
import { roadmapAPI } from '../api/roadmapAPI'
import { toast } from 'react-toastify'
import { currentDate } from '../../../utils/helpers'
import moment from 'moment'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)

const StyledListItem = styled(ListItem)(({ theme }) => ({
  border: '1px solid rgba(255, 255, 255, 0.15)',
  borderRadius: '12px',
  marginBottom: theme.spacing(1.5),
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderColor: 'rgba(255, 255, 255, 0.25)',
    transform: 'translateY(-2px)',
  },
}))

const LinkItemDialog = ({ open, onClose, ideaId, onLinkCreated }) => {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  const [tabValue, setTabValue] = useState(0)
  const [goals, setGoals] = useState([])
  const [projects, setProjects] = useState([])
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(false)
  const [linkType, setLinkType] = useState('GOAL')
  const [showCreate, setShowCreate] = useState({ goal: false, project: false, task: false })
  const [objectifsMetadata, setObjectifsMetadata] = useState({ domains: [], predefinedObjectives: [], frequency: [] })
  const [domains, setDomains] = useState([])
  const [categories, setCategories] = useState([])
  const [createPayload, setCreatePayload] = useState({
    goal: { domain: '', objectives_name: '', objectifs: '', date_objectives: currentDate(), periodicity: '' },
    project: { Name: '', LifeDomain: '' },
    task: { Nom: '', Categorie: '' },
  })
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    if (open) {
      if (tabValue === 0) fetchGoals()
      else if (tabValue === 1) fetchProjects()
      else if (tabValue === 2) fetchTasks()
    }
  }, [open, tabValue])

  useEffect(() => {
    if (open) {
      fetchObjectifsMetadata()
      fetchDomains()
      fetchCategories()
    }
  }, [open])

  const fetchGoals = async () => {
    setLoading(true)
    try {
      const response = await makeGetRequest(urls.crudMesObjectifs, {}, true)
      if (response.status === 200) {
        const data = response.data.results || response.data || []
        setGoals(Array.isArray(data) ? data : [])
      }
    } catch (error) {
      console.error('Error fetching goals:', error)
      setGoals([])
    } finally {
      setLoading(false)
    }
  }

  const fetchProjects = async () => {
    setLoading(true)
    try {
      const response = await makeGetRequest(urls.projectOperations, {}, true)
      if (response.status === 200) {
        const data = response.data.results || response.data || []
        setProjects(Array.isArray(data) ? data : [])
      }
    } catch (error) {
      console.error('Error fetching projects:', error)
      setProjects([])
    } finally {
      setLoading(false)
    }
  }

  const fetchTasks = async () => {
    setLoading(true)
    try {
      const response = await makeGetRequest(urls.fetchTasks, {}, true)
      if (response.status === 200) {
        const data = response.data.results || response.data || []
        setTasks(Array.isArray(data) ? data : [])
      }
    } catch (error) {
      console.error('Error fetching tasks:', error)
      setTasks([])
    } finally {
      setLoading(false)
    }
  }

  const fetchObjectifsMetadata = async () => {
    try {
      const response = await makeGetRequest(urls.fetchMesObjectifsMetaData, {}, true)
      if (response.status === 200 && response.data) {
        const data = response.data
        const domains = (data.domains || []).map((v) => ({ label: v.domain, id: v.id }))
        const frequency = (data.frequency || []).map((v) => ({ label: v.name_of_periodicity, id: v.id }))
        const predefinedObjectives = (data.predefined_objectives || data.predefinedObjectives || []).map((v) => ({
          label: v.objectives_name,
          id: v.id,
          domain: v.domain,
          value: v.value_format,
        }))
        setObjectifsMetadata({ domains, frequency, predefinedObjectives })
      }
    } catch (error) {
      console.error('Error fetching objectifs metadata:', error)
    }
  }

  const fetchDomains = async () => {
    try {
      const response = await makeGetRequest(`${urls.getDomain}?limit=100000`, {}, true)
      if (response.status === 200 && Array.isArray(response.data)) {
        setDomains(response.data.map((v) => ({ label: v.domain, id: v.id })))
      }
    } catch (error) {
      console.error('Error fetching domains:', error)
    }
  }

  const fetchCategories = async () => {
    try {
      const response = await makeGetRequest('/api/time/get-category-dd', {}, true)
      if (response.status === 200 && Array.isArray(response.data)) {
        setCategories(response.data.map((v) => ({ label: v.name, id: v.id })))
      }
    } catch (error) {
      console.error('Error fetching categories:', error)
    }
  }

  const handleCreateAndLinkGoal = async () => {
    const p = createPayload.goal
    if (!p.domain || !p.objectives_name || !p.objectifs || !p.periodicity) {
      toast.error(t('roadmap.errFillAllRequired'))
      return
    }
    setCreating(true)
    try {
      const payload = {
        domain: typeof p.domain === 'object' ? p.domain.id : p.domain,
        objectives_name: typeof p.objectives_name === 'object' ? p.objectives_name.id : p.objectives_name,
        objectifs: p.objectifs,
        date_objectives: p.date_objectives,
        periodicity: typeof p.periodicity === 'object' ? p.periodicity.id : p.periodicity,
        unit_of_measurement: (typeof p.objectives_name === 'object' && p.objectives_name?.value) ? p.objectives_name.value : '',
      }
      const response = await makePostRequest(urls.crudMesObjectifs, payload, {}, true)
      if (response.status === 201 && response.data?.id) {
        await roadmapAPI.createLink(ideaId, null, response.data.id, 'GOAL', 'goal')
        toast.success(t('roadmap.toastGoalCreatedLinked'))
        setShowCreate((s) => ({ ...s, goal: false }))
        setCreatePayload((c) => ({ ...c, goal: { domain: '', objectives_name: '', objectifs: '', date_objectives: currentDate(), periodicity: '' } }))
        fetchGoals()
        if (onLinkCreated) onLinkCreated()
      }
    } catch (error) {
      console.error('Error creating goal:', error)
      toast.error(t('roadmap.toastErrGoalCreate'))
    } finally {
      setCreating(false)
    }
  }

  const handleCreateAndLinkProject = async () => {
    const p = createPayload.project
    const lifeDomainId = typeof p.LifeDomain === 'object' ? p.LifeDomain?.id : p.LifeDomain
    if (!p.Name?.trim() || !lifeDomainId) {
      toast.error(t('roadmap.errNameDomainRequired'))
      return
    }
    setCreating(true)
    try {
      const payload = { Name: p.Name.trim(), LifeDomain: lifeDomainId }
      const response = await makePostRequest(urls.projectOperations, payload, {}, true)
      if (response.status === 201 && response.data?.id) {
        await roadmapAPI.createLink(ideaId, null, response.data.id, 'PROJECT', 'project')
        toast.success(t('roadmap.toastProjectCreatedLinked'))
        setShowCreate((s) => ({ ...s, project: false }))
        setCreatePayload((c) => ({ ...c, project: { Name: '', LifeDomain: '' } }))
        fetchProjects()
        if (onLinkCreated) onLinkCreated()
      }
    } catch (error) {
      console.error('Error creating project:', error)
      toast.error(t('roadmap.toastErrProjectCreate'))
    } finally {
      setCreating(false)
    }
  }

  const handleCreateAndLinkTask = async () => {
    const p = createPayload.task
    const categorieId = typeof p.Categorie === 'object' ? p.Categorie?.id : p.Categorie
    if (!p.Nom?.trim() || !categorieId) {
      toast.error(t('roadmap.toastErrNameCategoryRequired'))
      return
    }
    setCreating(true)
    try {
      const payload = { Nom: p.Nom.trim(), Categorie: categorieId }
      const response = await makePostRequest(urls.taskOperations, payload, {}, true)
      if (response.status === 201 && response.data?.id) {
        await roadmapAPI.createLink(ideaId, null, response.data.id, 'TASK', 'task')
        toast.success(t('roadmap.toastTaskCreatedLinked'))
        setShowCreate((s) => ({ ...s, task: false }))
        setCreatePayload((c) => ({ ...c, task: { Nom: '', Categorie: '' } }))
        fetchTasks()
        if (onLinkCreated) onLinkCreated()
      }
    } catch (error) {
      console.error('Error creating task:', error)
      toast.error(t('roadmap.toastErrTaskCreate'))
    } finally {
      setCreating(false)
    }
  }

  const handleLinkItem = async (itemId, modelName) => {
    if (!ideaId) {
      toast.error(t('roadmap.toastErrIdeaIdMissing'))
      return
    }

    try {
      setLoading(true)
      
      // Map frontend model names to backend model_name values
      // Backend supports: 'goal', 'project', 'task', 'userobjective', 'tache'
      const modelNameMap = {
        'goal': 'goal',        // Backend accepts 'goal' or 'userobjective'
        'project': 'project',
        'task': 'task'         // Backend also accepts 'tache'
      }
      
      const backendModelName = modelNameMap[modelName] || modelName
      
      // Use model_name directly - backend will resolve ContentType automatically
      await roadmapAPI.createLink(ideaId, null, itemId, linkType, backendModelName)
      
      toast.success(t('roadmap.toastLinkCreated'))
      if (onLinkCreated) {
        onLinkCreated()
      }
      onClose()
    } catch (error) {
      console.error('Error creating link:', error)
      toast.error(t('roadmap.toastErrLinkCreate'))
    } finally {
      setLoading(false)
    }
  }

  const getItemName = (item, type) => {
    if (type === 'goal') {
      return item.objectives_name?.objectives_name || item.objectives_name || t('roadmap.goalSingular')
    } else if (type === 'project') {
      return item.Name || t('roadmap.projectSingular')
    } else if (type === 'task') {
      return item.Nom || t('roadmap.taskCapital')
    }
    return t('roadmap.item')
  }

  return (
    <>
      <Backdrop
        open={open}
        sx={{
          zIndex: (theme) => theme.zIndex.modal - 1,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backgroundImage: 'url(/small.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backdropFilter: 'blur(10px)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 29, 0.6)',
          },
        }}
      />
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(15, 15, 25, 0.95)',
            backdropFilter: 'blur(30px)',
            borderRadius: '20px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            position: 'relative',
            zIndex: (theme) => theme.zIndex.modal,
          },
        }}
      >
      <DialogTitle 
        sx={{ 
          color: 'white',
          fontSize: { xs: '1.3rem', md: '1.6rem' },
          fontWeight: 600,
          textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8), -1px -1px 2px rgba(0, 0, 0, 0.8), 1px 1px 2px rgba(0, 0, 0, 0.8)',
        }}
      >
        {t('roadmap.linkItemTitle')}
      </DialogTitle>
      <DialogContent
        sx={{
          // Custom scrollbar styling (Word/Google Docs style)
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.5)',
            },
          },
          // Firefox scrollbar
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.05)',
        }}
      >
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => {
            setTabValue(newValue)
            setLinkType(newValue === 0 ? 'GOAL' : newValue === 1 ? 'PROJECT' : 'TASK')
          }}
          sx={{ 
            mb: 2,
            '& .MuiTab-root': {
              color: 'rgba(255, 255, 255, 0.7)',
              fontWeight: 500,
              fontSize: { xs: '0.9rem', md: '1rem' },
            },
            '& .Mui-selected': {
              color: 'white',
              fontWeight: 600,
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#1976d2',
            },
          }}
        >
          <Tab label={t('roadmap.tabGoals')} />
          <Tab label={t('roadmap.tabProjects')} />
          <Tab label={t('roadmap.tabTasks')} />
        </Tabs>

        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mb: 2 }} />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
            {tabValue === 0 && (
              <>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setShowCreate((s) => ({ ...s, goal: !s.goal }))}
                    sx={{ color: '#1976d2', borderColor: 'rgba(255,255,255,0.3)' }}
                    variant="outlined"
                  >
                    {showCreate.goal ? t('roadmap.cancel') : t('roadmap.createGoalAndLink')}
                  </Button>
                </Box>
                {showCreate.goal && (
                  <Box sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <Typography variant="subtitle2" sx={{ color: 'white', mb: 1.5 }}>{t('roadmap.newGoal')}</Typography>
                    <TextField select fullWidth size="small" label={t('roadmap.fieldDomain')} value={createPayload.goal.domain?.id ?? createPayload.goal.domain ?? ''} onChange={(e) => setCreatePayload((c) => ({ ...c, goal: { ...c.goal, domain: objectifsMetadata.domains.find((d) => d.id === e.target.value) || e.target.value, objectives_name: '' } }))} sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { color: 'white' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' } }}>
                      {objectifsMetadata.domains.map((d) => (
                        <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>
                      ))}
                    </TextField>
                    <TextField select fullWidth size="small" label={t('roadmap.goalSingular')} value={createPayload.goal.objectives_name?.id ?? createPayload.goal.objectives_name ?? ''} onChange={(e) => setCreatePayload((c) => ({ ...c, goal: { ...c.goal, objectives_name: objectifsMetadata.predefinedObjectives.find((o) => o.id === e.target.value) || e.target.value } }))} sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { color: 'white' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' } }}>
                      {(objectifsMetadata.predefinedObjectives || []).filter((o) => !createPayload.goal.domain || o.domain === (createPayload.goal.domain?.id ?? createPayload.goal.domain)).map((o) => (
                        <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>
                      ))}
                    </TextField>
                    <TextField type="number" fullWidth size="small" label={t('roadmap.fieldValue')} value={createPayload.goal.objectifs} onChange={(e) => setCreatePayload((c) => ({ ...c, goal: { ...c.goal, objectifs: e.target.value } }))} sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { color: 'white' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' } }} />
                    <TextField type="date" fullWidth size="small" label={t('roadmap.fieldGoalDate')} value={createPayload.goal.date_objectives || moment().add(1, 'year').format('YYYY-MM-DD')} onChange={(e) => setCreatePayload((c) => ({ ...c, goal: { ...c.goal, date_objectives: e.target.value } }))} InputLabelProps={{ shrink: true }} sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { color: 'white' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' } }} />
                    <TextField select fullWidth size="small" label={t('roadmap.fieldPeriodicity')} value={createPayload.goal.periodicity?.id ?? createPayload.goal.periodicity ?? ''} onChange={(e) => setCreatePayload((c) => ({ ...c, goal: { ...c.goal, periodicity: objectifsMetadata.frequency.find((f) => f.id === e.target.value) || e.target.value } }))} sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { color: 'white' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' } }}>
                      {(objectifsMetadata.frequency || []).map((f) => (
                        <MenuItem key={f.id} value={f.id}>{f.label}</MenuItem>
                      ))}
                    </TextField>
                    <Button size="small" variant="contained" onClick={handleCreateAndLinkGoal} disabled={creating}>{creating ? <CircularProgress size={20} /> : t('roadmap.createAndLink')}</Button>
                  </Box>
                )}
                {goals.length === 0 && !showCreate.goal ? (
                  <Typography 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.6)', 
                      textAlign: 'center', 
                      p: 3,
                      fontSize: { xs: '0.9rem', md: '1rem' },
                    }}
                  >
                    {t('roadmap.noGoalAvailable')}
                  </Typography>
                ) : (
                  <List>
                    {goals.map((goal) => (
                      <StyledListItem key={goal.id}>
                        <ListItemButton onClick={() => handleLinkItem(goal.id, 'goal')}>
                          <ListItemText
                            primary={getItemName(goal, 'goal')}
                            secondary={goal.domain?.name || t('roadmap.goalSingular')}
                            sx={{ 
                              color: 'white',
                              '& .MuiListItemText-primary': {
                                fontWeight: 500,
                                fontSize: { xs: '0.95rem', md: '1rem' },
                              },
                              '& .MuiListItemText-secondary': {
                                color: 'rgba(255, 255, 255, 0.7)',
                                fontSize: { xs: '0.85rem', md: '0.9rem' },
                              },
                            }}
                          />
                        </ListItemButton>
                      </StyledListItem>
                    ))}
                  </List>
                )}
              </>
            )}
            {tabValue === 1 && (
              <>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setShowCreate((s) => ({ ...s, project: !s.project }))}
                    sx={{ color: '#1976d2', borderColor: 'rgba(255,255,255,0.3)' }}
                    variant="outlined"
                  >
                    {showCreate.project ? t('roadmap.cancel') : t('roadmap.createProjectAndLink')}
                  </Button>
                </Box>
                {showCreate.project && (
                  <Box sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <Typography variant="subtitle2" sx={{ color: 'white', mb: 1.5 }}>{t('roadmap.newProject')}</Typography>
                    <TextField fullWidth size="small" label={t('roadmap.fieldProjectName')} value={createPayload.project.Name} onChange={(e) => setCreatePayload((c) => ({ ...c, project: { ...c.project, Name: e.target.value } }))} sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { color: 'white' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' } }} />
                    <TextField select fullWidth size="small" label={t('roadmap.fieldDomain')} value={createPayload.project.LifeDomain?.id ?? createPayload.project.LifeDomain ?? ''} onChange={(e) => setCreatePayload((c) => ({ ...c, project: { ...c.project, LifeDomain: domains.find((d) => d.id === e.target.value) || e.target.value } }))} sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { color: 'white' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' } }}>
                      {domains.map((d) => (
                        <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>
                      ))}
                    </TextField>
                    <Button size="small" variant="contained" onClick={handleCreateAndLinkProject} disabled={creating}>{creating ? <CircularProgress size={20} /> : t('roadmap.createAndLink')}</Button>
                  </Box>
                )}
                {projects.length === 0 && !showCreate.project ? (
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', textAlign: 'center', p: 3 }}>
                    {t('roadmap.noProjectAvailable')}
                  </Typography>
                ) : (
                  <List>
                    {projects.map((project) => (
                      <StyledListItem key={project.id}>
                        <ListItemButton onClick={() => handleLinkItem(project.id, 'project')}>
                          <ListItemText
                            primary={getItemName(project, 'project')}
                            secondary={project.LifeDomain?.name || t('roadmap.projectSingular')}
                            sx={{ 
                              color: 'white',
                              '& .MuiListItemText-primary': {
                                fontWeight: 500,
                                fontSize: { xs: '0.95rem', md: '1rem' },
                              },
                              '& .MuiListItemText-secondary': {
                                color: 'rgba(255, 255, 255, 0.7)',
                                fontSize: { xs: '0.85rem', md: '0.9rem' },
                              },
                            }}
                          />
                        </ListItemButton>
                      </StyledListItem>
                    ))}
                  </List>
                )}
              </>
            )}
            {tabValue === 2 && (
              <>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setShowCreate((s) => ({ ...s, task: !s.task }))}
                    sx={{ color: '#1976d2', borderColor: 'rgba(255,255,255,0.3)' }}
                    variant="outlined"
                  >
                    {showCreate.task ? t('roadmap.cancel') : t('roadmap.createTaskAndLink')}
                  </Button>
                </Box>
                {showCreate.task && (
                  <Box sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <Typography variant="subtitle2" sx={{ color: 'white', mb: 1.5 }}>{t('roadmap.newTask')}</Typography>
                    <TextField fullWidth size="small" label={t('roadmap.fieldTaskName')} value={createPayload.task.Nom} onChange={(e) => setCreatePayload((c) => ({ ...c, task: { ...c.task, Nom: e.target.value } }))} sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { color: 'white' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' } }} />
                    <TextField select fullWidth size="small" label={t('roadmap.fieldCategory')} value={createPayload.task.Categorie?.id ?? createPayload.task.Categorie ?? ''} onChange={(e) => setCreatePayload((c) => ({ ...c, task: { ...c.task, Categorie: categories.find((cat) => cat.id === e.target.value) || e.target.value } }))} sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { color: 'white' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' } }}>
                      {categories.map((cat) => (
                        <MenuItem key={cat.id} value={cat.id}>{cat.label}</MenuItem>
                      ))}
                    </TextField>
                    <Button size="small" variant="contained" onClick={handleCreateAndLinkTask} disabled={creating}>{creating ? <CircularProgress size={20} /> : t('roadmap.createAndLink')}</Button>
                  </Box>
                )}
                {tasks.length === 0 && !showCreate.task ? (
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', textAlign: 'center', p: 3 }}>
                    {t('roadmap.noTaskAvailable')}
                  </Typography>
                ) : (
                  <List>
                    {tasks.map((task) => (
                      <StyledListItem key={task.id}>
                        <ListItemButton onClick={() => handleLinkItem(task.id, 'task')}>
                          <ListItemText
                            primary={getItemName(task, 'task')}
                            secondary={task.Categorie?.name || t('roadmap.taskCapital')}
                            sx={{ 
                              color: 'white',
                              '& .MuiListItemText-primary': {
                                fontWeight: 500,
                                fontSize: { xs: '0.95rem', md: '1rem' },
                              },
                              '& .MuiListItemText-secondary': {
                                color: 'rgba(255, 255, 255, 0.7)',
                                fontSize: { xs: '0.85rem', md: '0.9rem' },
                              },
                            }}
                          />
                        </ListItemButton>
                      </StyledListItem>
                    ))}
                  </List>
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={onClose} 
          sx={{ 
            color: 'rgba(255, 255, 255, 0.85)',
            borderColor: 'rgba(255, 255, 255, 0.3)',
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.5)',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
            },
          }}
          variant="outlined"
        >
          {t('roadmap.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  )
}

export default LinkItemDialog

